import { useId, type InputHTMLAttributes, type ReactNode } from 'react';
import styles from './TextField.module.css';

export interface TextFieldProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'value' | 'size'
> {
  label: ReactNode;
  value: string;
  onChange: (value: string) => void;
  hint?: ReactNode;
  /** A problem with the value: shown under the field and announced. */
  error?: ReactNode;
}

/** A labelled text input. The label is always visible; hint and error are tied to the input for screen readers. */
export function TextField({
  label,
  value,
  onChange,
  hint,
  error,
  className,
  id: idProp,
  ...rest
}: TextFieldProps) {
  const generated = useId();
  const id = idProp ?? generated;
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint ? hintId : '', error ? errorId : ''].filter(Boolean).join(' ');
  return (
    <div className={[styles['field'], className ?? ''].filter(Boolean).join(' ')}>
      <label className={styles['label']} htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className={styles['input']}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        {...rest}
      />
      {hint ? (
        <span id={hintId} className={styles['hint']}>
          {hint}
        </span>
      ) : null}
      {error ? (
        <span id={errorId} className={styles['error']} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}
